<script setup>
import { groupDestinations } from 'dashboard/routes/dashboard/hub/hubWorlds';
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useSmallScreen } from 'dashboard/composables/useSmallScreen';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { workspacePreferenceFailed } from 'dashboard/composables/useWorkspaceV2';
import { useHubWorlds } from 'dashboard/routes/dashboard/hub/useHubWorlds';
import {
  useWorkspaceNavigation,
  WORKSPACE_MODES,
  orderWorkspaceItems,
} from 'dashboard/composables/useWorkspaceNavigation';
import { workspaceV2Path } from 'dashboard/helper/workspaceV2';
import {
  QUICK_DESTINATION_IDS,
  quickDestinationsFor,
  quickToolForRoute,
  readQuickDestination,
  writeQuickDestination,
} from 'dashboard/helper/workspaceV2QuickNavigation';
import { matchHubDestinations } from 'dashboard/routes/dashboard/hub/hubSearch';
import { emitter } from 'shared/helpers/mitt';
import SidebarAccountSwitcher from 'dashboard/components-next/sidebar/SidebarAccountSwitcher.vue';
import SidebarProfileMenu from 'dashboard/components-next/sidebar/SidebarProfileMenu.vue';
import FocusModeSettings from 'dashboard/components-next/sidebar/FocusModeSettings.vue';
import NavIcon from 'dashboard/components-next/icon/NavIcon.vue';
import SettingsContextBar from 'dashboard/routes/dashboard/settings/SettingsContextBar.vue';
import {
  settingsCenterTarget,
  settingsArea,
  inheritSettingsContext,
} from 'dashboard/routes/dashboard/settings/settingsContext';

const emit = defineEmits(['showCreateAccountModal', 'openKeyShortcutModal']);
// Escala de raios do guia Visor no V2, inclusive em menus teleportados para
// o body: chip 6, campo 10, botão 12, bloco 16, modal 20 e destaque 24 vêm
// das próprias classes; aqui só campo e botão são fixados. Pílula fica para
// segmentado e contador (filhos do botão voltam ao valor da classe).
const radiusClasses = [
  '[&_input]:[--workspace-radius:10px]',
  '[&_input]:[--workspace-radius-full:10px]',
  '[&_textarea]:[--workspace-radius:10px]',
  '[&_textarea]:[--workspace-radius-full:10px]',
  '[&_select]:[--workspace-radius:10px]',
  '[&_select]:[--workspace-radius-full:10px]',
  '[&_button]:[--workspace-radius-full:12px]',
  '[&_button>*]:[--workspace-radius-full:initial]',
  '[&_[role=tablist]_button]:[--workspace-radius-full:initial]',
];
onMounted(() => document.body.classList.add(...radiusClasses));
onBeforeUnmount(() => document.body.classList.remove(...radiusClasses));

const { t } = useI18n();
const { isAccountAdmin, isUserView, toggleUserView } = useAdmin();
const route = useRoute();
const { isSmallScreen } = useSmallScreen();
const isMobileConversation = computed(
  () =>
    isSmallScreen.value &&
    Boolean(route.params.conversation_id || route.params.conversationId)
);
const router = useRouter();
const removeSettingsGuard = router.beforeEach(inheritSettingsContext);
onBeforeUnmount(removeSettingsGuard);
const { visibleWorlds, preferenceWorlds } = useHubWorlds();
const {
  mode,
  order,
  saving,
  failed,
  setMode,
  setOrder,
  setWorldVisible,
  reset,
} = useWorkspaceNavigation();
const worlds = computed(() =>
  orderWorkspaceItems(visibleWorlds.value, order.value)
);
const allWorlds = computed(() =>
  orderWorkspaceItems(preferenceWorlds.value, order.value)
);
// Barra superior: Conversas, Kanban, Tarefas e Agenda. A Agenda entra
// ao lado dos três atalhos que a produção já tinha.
const quickDestinations = computed(() =>
  quickDestinationsFor(visibleWorlds.value)
);
const lastQuickDestinations = ref({});
const accountId = computed(() => String(route.params.accountId || ''));
const reloadQuickDestinations = id => {
  lastQuickDestinations.value = Object.fromEntries(
    QUICK_DESTINATION_IDS.map(destinationId => [
      destinationId,
      readQuickDestination(id, destinationId),
    ])
  );
};
watch(accountId, reloadQuickDestinations, { immediate: true });
watch(
  [() => route.fullPath, visibleWorlds],
  ([fullPath]) => {
    const destinationId = quickToolForRoute(route, visibleWorlds.value);
    if (!destinationId || !accountId.value) return;
    writeQuickDestination(accountId.value, destinationId, fullPath);
    lastQuickDestinations.value = {
      ...lastQuickDestinations.value,
      [destinationId]: fullPath,
    };
  },
  { immediate: true }
);
const hubPath = computed(
  () => `/app/accounts/${route.params.accountId}/v2/hub`
);
const organizeDialog = ref(null);
// Organizar mora no rodapé da Central e nas configurações de perfil.
// A engrenagem do topo abre a conta/perfil, não este painel.
const openOrganize = () => organizeDialog.value?.showModal();
onMounted(() => emitter.on('workspace:organize:open', openOrganize));
onBeforeUnmount(() => emitter.off('workspace:organize:open', openOrganize));
const selectedConfigFunnel = useMapGetter('funnel/getSelectedFunnel');
// Não lidas do chat interno no menu lateral, o mesmo número do tile da
// Central: o store teamChat soma as salas do chat interno e é atualizado na
// carga da conta, a cada intervalo e quando uma sala é lida.
const internalChatUnread = useMapGetter('teamChat/getUnreadCount');
const unreadFor = destination =>
  destination.id === 'internal_chat'
    ? Number(internalChatUnread.value) || 0
    : 0;
const centerTarget = computed(() =>
  settingsCenterTarget(
    route,
    route.name === 'kanban_board' && selectedConfigFunnel.value?.id
      ? { funnel_id: String(selectedConfigFunnel.value.id) }
      : {}
  )
);
// A engrenagem abre todas as configurações; o "Configurar" abre as da área em
// que a pessoa está. Antes os dois levavam ao mesmo lugar filtrado.
const allSettingsTarget = computed(() => {
  const target = centerTarget.value;
  const query = { ...target.query };
  delete query.config_area;
  return { ...target, query };
});
const contextualArea = computed(() =>
  route.query.config_center !== '1' && route.name !== 'settings_menu'
    ? settingsArea(route)
    : ''
);
const destinationsDialog = ref(null);
const search = ref('');
const headerCollapsed = ref(false);
const headerToggleLabel = computed(() =>
  t(
    headerCollapsed.value
      ? 'WORKSPACE_V2.SHOW_HEADER'
      : 'WORKSPACE_V2.HIDE_HEADER'
  )
);
const destinationPath = destination => {
  const resolved = router.resolve(destination.to);
  return workspaceV2Path(resolved.fullPath);
};
const matches = computed(() =>
  matchHubDestinations(allWorlds.value, search.value, t)
);
const quickDestinationPath = destination =>
  lastQuickDestinations.value[destination.id] || destinationPath(destination);
const isQuickDestinationCurrent = destination =>
  quickToolForRoute(route, visibleWorlds.value) === destination.id;
const advanced = ref(null);
const dragged = ref(null);
const move = (id, offset) => {
  const ids = allWorlds.value.map(world => world.id);
  const from = ids.indexOf(id);
  const to = from + offset;
  if (from < 0 || to < 0 || to >= ids.length) return;
  ids.splice(to, 0, ids.splice(from, 1)[0]);
  setOrder(ids);
};
const drop = id => {
  const ids = allWorlds.value.map(world => world.id);
  const from = ids.indexOf(dragged.value);
  const to = ids.indexOf(id);
  if (from >= 0 && to >= 0 && from !== to) {
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setOrder(ids);
  }
  dragged.value = null;
};
const matchesSubview = destination => {
  const keys = ['view', 'preset'].filter(
    key => destination.to.query?.[key] !== undefined
  );
  return (
    route.name === destination.to.name &&
    keys.length > 0 &&
    keys.every(
      key => String(route.query[key]) === String(destination.to.query[key])
    )
  );
};
// Destinos da mesma rota com parâmetro próprio (as áreas da Central da IA,
// todas `ai_center_area`) só acendem quando o parâmetro também bate.
const paramsMatch = destination =>
  Object.entries(destination.to.params || {}).every(
    ([key, value]) => String(route.params[key]) === String(value)
  );
const isCurrent = destination => {
  if (route.name !== destination.to.name) return false;
  if (!paramsMatch(destination)) return false;
  if (['view', 'preset'].some(key => destination.to.query?.[key] !== undefined))
    return matchesSubview(destination);
  return !allWorlds.value.some(world =>
    world.destinations.some(matchesSubview)
  );
};
const openAdvanced = () => {
  organizeDialog.value.close();
  advanced.value?.open();
};
</script>

<template>
  <div
    class="relative flex min-h-0 min-w-0 flex-1 flex-col bg-n-background text-[15px] text-n-slate-12"
    data-testid="workspace-v2-shell"
  >
    <button
      type="button"
      data-testid="workspace-header-toggle"
      class="absolute left-1/2 top-0 z-40 flex h-5 w-10 -translate-x-1/2 items-center justify-center rounded-b-lg border border-t-0 border-n-weak bg-n-background text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
      :aria-label="headerToggleLabel"
      :title="headerToggleLabel"
      :aria-expanded="!headerCollapsed"
      aria-controls="workspace-global-header"
      @click="headerCollapsed = !headerCollapsed"
    >
      <span
        :class="headerCollapsed ? 'i-ph-caret-down' : 'i-ph-caret-up'"
        class="size-4"
        aria-hidden="true"
      />
    </button>
    <header
      v-show="!headerCollapsed"
      id="workspace-global-header"
      class="flex h-14 shrink-0 items-center gap-0 border-b border-n-weak px-1 min-[380px]:gap-1 min-[380px]:px-2 sm:gap-2 sm:px-3 dark:border-n-weak"
    >
      <SidebarAccountSwitcher
        variant="icon"
        @show-create-account-modal="emit('showCreateAccountModal')"
      />
      <RouterLink
        :to="hubPath"
        :aria-label="t('WORKSPACE_V2.HUB')"
        class="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl px-3 py-2 font-medium transition-colors duration-150 hover:bg-n-hover active:bg-n-pressed"
      >
        <span class="i-ph-house size-5 sm:hidden" /><span
          class="hidden sm:inline"
          >{{ t('WORKSPACE_V2.TITLE') }}</span
        >
      </RouterLink>
      <nav
        v-if="quickDestinations.length"
        data-testid="workspace-v2-quick-navigation"
        :aria-label="t('WORKSPACE_V2.NAVIGATION')"
        class="mx-auto hidden min-w-0 shrink-0 items-center gap-0.5 overflow-x-auto rounded-xl border border-n-weak bg-n-alpha-black2 p-0.5 lg:flex dark:bg-n-solid-1"
      >
        <RouterLink
          v-for="destination in quickDestinations"
          :key="destination.id"
          :to="quickDestinationPath(destination)"
          :aria-current="
            isQuickDestinationCurrent(destination) ? 'page' : undefined
          "
          class="group/solar flex min-h-10 shrink-0 items-center gap-2 rounded-[10px] px-3.5 py-2 text-sm font-medium text-n-slate-11 transition-colors duration-150 hover:text-n-slate-12"
          :class="{
            'bg-n-solid-1 text-n-slate-12 ring-1 ring-n-weak dark:bg-n-solid-3':
              isQuickDestinationCurrent(destination),
          }"
          :data-testid="`workspace-v2-quick-${destination.id}`"
        >
          <NavIcon
            class="size-4"
            :icon="destination.icon"
            :active="isQuickDestinationCurrent(destination)"
          />
          <span>{{ destination.label || t(destination.labelKey) }}</span>
        </RouterLink>
      </nav>
      <div class="flex-1" />
      <button
        v-if="isAccountAdmin"
        type="button"
        data-testid="workspace-v2-admin-indicator"
        class="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-lg px-2 text-xs text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
        :title="
          t(
            isUserView
              ? 'WORKSPACE_V2.RETURN_ADMIN'
              : 'WORKSPACE_V2.PREVIEW_USER'
          )
        "
        :aria-label="
          t(
            isUserView
              ? 'WORKSPACE_V2.RETURN_ADMIN'
              : 'WORKSPACE_V2.PREVIEW_USER'
          )
        "
        :aria-pressed="Boolean(isUserView)"
        @click="
          toggleUserView();
          router.push(hubPath);
        "
      >
        <span class="i-lucide-eye size-4" aria-hidden="true" />
        <span class="hidden sm:inline">{{
          t(
            isUserView ? 'WORKSPACE_V2.USER_VIEW' : 'WORKSPACE_V2.ACCOUNT_ADMIN'
          )
        }}</span>
      </button>
      <button
        type="button"
        :aria-label="t('WORKSPACE_V2.SEARCH')"
        class="flex h-10 shrink-0 items-center gap-2 rounded-xl border border-transparent px-2 text-sm text-n-slate-11 transition-colors duration-150 hover:bg-n-hover lg:min-w-[220px] lg:border-n-field lg:bg-n-solid-1 lg:px-3 lg:hover:border-n-field-hover"
        data-testid="workspace-v2-search"
        @click="destinationsDialog.showModal()"
      >
        <span class="i-ph-magnifying-glass size-5" aria-hidden="true" />
        <span class="hidden lg:inline">{{
          t('WORKSPACE_V2.SEARCH_HINT')
        }}</span>
        <kbd
          class="ml-auto hidden rounded-md border border-n-weak px-1.5 py-0.5 text-[10px] font-medium lg:inline"
          >{{ t('WORKSPACE_V2.SEARCH_KBD') }}</kbd
        >
      </button>
      <RouterLink
        v-if="contextualArea"
        :to="centerTarget"
        data-testid="configure-current-area"
        class="hidden xl:inline-flex min-h-11 items-center gap-2 rounded-xl px-3 text-sm text-n-slate-11 hover:bg-n-alpha-2"
      >
        {{ t('SETTINGS_CENTER.CONFIGURE_AREA') }}
      </RouterLink>
      <RouterLink
        v-tooltip="t('SETTINGS_NAV.TITLE')"
        :to="allSettingsTarget"
        data-testid="workspace-v2-settings"
        :aria-label="t('SETTINGS_NAV.TITLE')"
        class="inline-flex size-11 shrink-0 items-center justify-center rounded-xl text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
      >
        <span class="i-ph-gear-six size-5" aria-hidden="true" />
      </RouterLink>
      <SidebarProfileMenu
        is-collapsed
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
    </header>
    <nav
      v-if="
        quickDestinations.length && !isMobileConversation && !headerCollapsed
      "
      data-testid="workspace-v2-quick-navigation-compact"
      :aria-label="t('WORKSPACE_V2.NAVIGATION')"
      class="flex min-w-0 shrink-0 items-center gap-1 overflow-x-auto border-b border-n-weak px-2 py-1 lg:hidden dark:border-n-weak"
    >
      <RouterLink
        v-for="destination in quickDestinations"
        :key="destination.id"
        :to="quickDestinationPath(destination)"
        :aria-current="
          isQuickDestinationCurrent(destination) ? 'page' : undefined
        "
        class="group/solar flex min-h-10 min-w-0 shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors duration-150 hover:bg-n-hover active:bg-n-pressed"
        :class="{
          'font-medium text-n-blue-11': isQuickDestinationCurrent(destination),
        }"
      >
        <NavIcon
          class="size-4"
          :icon="destination.icon"
          :active="isQuickDestinationCurrent(destination)"
        />
        <span class="min-w-0 truncate">{{
          destination.label || t(destination.labelKey)
        }}</span>
      </RouterLink>
    </nav>
    <p
      v-if="workspacePreferenceFailed"
      role="alert"
      class="m-0 border-b border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-200"
    >
      {{ t('WORKSPACE_V2.SAVE_FAILED') }}
    </p>
    <div class="flex min-h-0 min-w-0 flex-1">
      <aside
        :inert="mode !== 'sidebar' ? '' : null"
        :aria-hidden="mode !== 'sidebar'"
        class="hidden shrink-0 overflow-hidden transition-[width] duration-200 ease-out motion-reduce:transition-none xl:block"
        :class="mode === 'sidebar' ? 'w-[264px]' : 'w-0'"
      >
        <nav
          :aria-label="t('WORKSPACE_V2.NAVIGATION')"
          class="h-full w-[264px] overflow-y-auto border-r border-n-weak bg-n-solid-1 p-3 md:block"
        >
          <section v-for="world in worlds" :key="world.id" class="mb-5">
            <h2 class="mb-1 px-3 text-xs font-medium text-n-slate-11">
              {{ t(world.labelKey) }}
            </h2>
            <section
              v-for="group in groupDestinations(world.destinations)"
              :key="group.id"
              class="mb-3"
            >
              <h3
                v-if="groupDestinations(world.destinations).length > 1"
                class="m-0 px-3 py-2 text-xs font-medium text-n-slate-11"
              >
                {{ t(group.labelKey) }}
              </h3>
              <RouterLink
                v-for="destination in group.destinations"
                :key="destination.id"
                :to="destinationPath(destination)"
                :aria-current="isCurrent(destination) ? 'page' : undefined"
                class="group/solar flex min-h-11 items-center gap-3 rounded-xl px-3 py-2 text-[15px] transition-colors duration-150 hover:bg-n-hover active:bg-n-pressed"
                :class="{
                  'bg-n-blue-9/10 font-medium text-n-blue-11':
                    isCurrent(destination),
                }"
              >
                <NavIcon
                  class="size-6 shrink-0"
                  :icon="destination.icon"
                  :active="isCurrent(destination)"
                />{{ destination.label || t(destination.labelKey) }}
                <span
                  v-if="unreadFor(destination)"
                  :data-testid="`workspace-v2-unread-${destination.id}`"
                  class="ml-auto grid h-[22px] min-w-[22px] shrink-0 place-items-center rounded-full bg-n-blue-9/10 px-1.5 text-[11px] font-medium tabular-nums text-n-blue-11"
                >
                  <span aria-hidden="true">{{ unreadFor(destination) }}</span>
                  <span class="sr-only">{{
                    t('HUB.ATTENDANCE.UNREAD', unreadFor(destination), {
                      count: unreadFor(destination),
                    })
                  }}</span>
                </span>
              </RouterLink>
            </section>
          </section>
        </nav>
      </aside>
      <div class="flex min-h-0 min-w-0 flex-1 flex-col">
        <SettingsContextBar />
        <slot />
      </div>
    </div>
    <nav
      v-if="!isMobileConversation && (mode === 'focus' || mode === 'sidebar')"
      :aria-label="t('WORKSPACE_V2.NAVIGATION')"
      class="flex shrink-0 items-center justify-start gap-1 overflow-x-auto md:justify-center border-t border-n-weak p-2 dark:border-n-weak"
      :class="{ 'xl:hidden': mode === 'sidebar' }"
    >
      <RouterLink
        :to="hubPath"
        class="group/solar flex shrink-0 flex-col items-center gap-1 rounded-xl px-3 py-2 text-xs hover:bg-n-hover active:bg-n-pressed"
      >
        <NavIcon class="size-5" icon="i-ph-house" />{{ t('WORKSPACE_V2.HUB') }}
      </RouterLink>
      <RouterLink
        v-for="world in worlds"
        :key="world.id"
        :to="destinationPath(world.primaryDestination || world.destinations[0])"
        class="group/solar flex shrink-0 flex-col items-center gap-1 rounded-xl px-3 py-2 text-xs transition-colors duration-150 hover:bg-n-hover active:bg-n-pressed"
      >
        <NavIcon class="size-5" :icon="world.icon" />{{ t(world.labelKey) }}
      </RouterLink>
    </nav>
    <dialog
      ref="organizeDialog"
      :aria-label="t('WORKSPACE_V2.ORGANIZE_TITLE')"
      aria-modal="true"
      class="m-auto max-h-[85dvh] w-[min(92vw,552px)] overflow-y-auto rounded-[20px] border border-n-weak bg-n-solid-1 p-6 text-n-slate-12 shadow-xl backdrop:bg-n-modal-overlay dark:border-n-strong dark:bg-n-solid-2 dark:shadow-none font-wix-text"
    >
      <div class="mb-2 flex items-center justify-between gap-4">
        <h2 class="m-0 text-xl font-medium">
          {{ t('WORKSPACE_V2.ORGANIZE_TITLE') }}
        </h2>
        <button
          type="button"
          :aria-label="t('WORKSPACE_V2.CLOSE')"
          class="grid size-11 place-items-center rounded-full hover:bg-n-hover active:bg-n-pressed"
          @click="organizeDialog.close()"
        >
          <span class="i-ph-x size-5" />
        </button>
      </div>
      <p class="mb-5 text-sm text-n-slate-11 dark:text-n-slate-11">
        {{ t('WORKSPACE_V2.PERSONAL') }}
      </p>
      <fieldset class="m-0 border-0 p-0">
        <legend class="mb-2 text-sm font-medium">
          {{ t('WORKSPACE_V2.NAVIGATION') }}
        </legend>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="item in WORKSPACE_MODES"
            :key="item"
            type="button"
            :aria-pressed="mode === item"
            :disabled="saving"
            class="rounded-xl border px-2 py-3 text-sm transition-colors duration-150 disabled:opacity-50"
            :class="
              mode === item
                ? 'border-n-blue-9 bg-n-blue-9/10 text-n-blue-9'
                : 'border-n-strong hover:bg-n-hover active:bg-n-pressed'
            "
            @click="setMode(item)"
          >
            {{ t(`WORKSPACE_V2.MODES.${item.toUpperCase()}`) }}
          </button>
        </div>
      </fieldset>
      <h3 class="mb-1 mt-6 text-sm font-medium">
        {{ t('WORKSPACE_V2.ORDER') }}
      </h3>
      <p class="mb-3 text-sm text-n-slate-11 dark:text-n-slate-11">
        {{ t('WORKSPACE_V2.DRAG_HINT') }}
      </p>
      <div
        v-for="(world, index) in allWorlds"
        :key="world.id"
        :draggable="!saving"
        class="flex items-center gap-2 rounded-lg border border-transparent p-2 hover:border-n-strong"
        @dragstart="dragged = world.id"
        @dragend="dragged = null"
        @dragover.prevent
        @drop.prevent="drop(world.id)"
      >
        <span
          class="i-ph-dots-six-vertical size-4 shrink-0 cursor-grab text-n-slate-11"
          aria-hidden="true"
        />
        <label class="flex flex-1 items-center gap-3 text-sm"
          ><input
            type="checkbox"
            :checked="!world.hidden"
            :disabled="saving"
            @change="setWorldVisible(world.id, $event.target.checked)"
          />{{ t(world.labelKey) }}</label
        >
        <button
          type="button"
          :disabled="saving || index === 0"
          :aria-label="t('WORKSPACE_V2.MOVE_UP', { name: t(world.labelKey) })"
          class="grid size-11 place-items-center rounded-lg hover:bg-n-hover active:bg-n-pressed disabled:opacity-20"
          @click="move(world.id, -1)"
        >
          <span class="i-ph-caret-up size-4" />
        </button>
        <button
          type="button"
          :disabled="saving || index === allWorlds.length - 1"
          :aria-label="t('WORKSPACE_V2.MOVE_DOWN', { name: t(world.labelKey) })"
          class="grid size-11 place-items-center rounded-lg hover:bg-n-hover active:bg-n-pressed disabled:opacity-20"
          @click="move(world.id, 1)"
        >
          <span class="i-ph-caret-down size-4" />
        </button>
      </div>
      <p v-if="failed" role="alert" class="mt-3 text-sm text-red-600">
        {{ t('WORKSPACE_V2.SAVE_FAILED') }}
      </p>
      <div
        class="mt-5 flex flex-wrap items-center gap-2 border-t border-n-weak pt-4 dark:border-n-weak"
      >
        <button
          type="button"
          class="rounded-xl bg-n-blue-9 px-4 py-2 text-sm font-medium text-white hover:bg-n-blue-10"
          @click="openAdvanced"
        >
          {{ t('WORKSPACE_V2.DETAILS') }}</button
        ><button
          type="button"
          :disabled="saving"
          class="rounded-xl px-4 py-2 text-sm hover:bg-n-hover active:bg-n-pressed"
          @click="reset"
        >
          {{ t('WORKSPACE_V2.RESET') }}
        </button>
      </div>
    </dialog>
    <dialog
      ref="destinationsDialog"
      :aria-label="t('WORKSPACE_V2.SEARCH')"
      aria-modal="true"
      class="m-auto max-h-[80dvh] w-[min(92vw,552px)] overflow-y-auto rounded-[20px] border border-n-weak bg-n-solid-1 p-4 text-n-slate-12 shadow-xl backdrop:bg-n-modal-overlay dark:border-n-strong dark:bg-n-solid-2 dark:shadow-none font-wix-text"
    >
      <div class="mb-3 flex items-center gap-3">
        <input
          v-model="search"
          autofocus
          type="search"
          :aria-label="t('WORKSPACE_V2.SEARCH')"
          :placeholder="t('WORKSPACE_V2.SEARCH')"
          class="!m-0 flex-1 !rounded-[10px] !outline-n-field hover:!outline-n-field-hover !bg-transparent !text-[15px]"
        /><button
          type="button"
          :aria-label="t('WORKSPACE_V2.CLOSE')"
          class="grid size-11 place-items-center rounded-full hover:bg-n-hover active:bg-n-pressed"
          @click="destinationsDialog.close()"
        >
          <span class="i-ph-x size-5" />
        </button>
      </div>
      <p class="px-2 text-sm text-n-slate-11 dark:text-n-slate-11">
        {{ t('WORKSPACE_V2.ALL_DESTINATIONS') }}
      </p>
      <RouterLink
        v-for="destination in matches"
        :key="destination.key"
        :to="destinationPath(destination)"
        class="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-n-hover active:bg-n-pressed"
        @click="destinationsDialog.close()"
      >
        <span :class="destination.icon" class="size-5 shrink-0" /><span
          class="flex-1 text-[15px]"
          >{{ destination.label || t(destination.labelKey) }}</span
        ><span class="text-xs text-n-slate-11 dark:text-n-slate-11">{{
          destination.worldLabel
        }}</span>
      </RouterLink>
      <p
        v-if="!matches.length"
        role="status"
        class="p-3 text-sm text-n-slate-11 dark:text-n-slate-11"
      >
        {{ t('WORKSPACE_V2.NO_RESULTS') }}
      </p>
    </dialog>
    <FocusModeSettings ref="advanced" />
  </div>
</template>
